import { Fragment, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import JsonLD from "@/components/base/JsonLD";
import mortgageContent from "@/content/services/kredyt-hipoteczny-sanok.md?raw";
import cashContent from "@/content/services/kredyt-gotowkowy-sanok.md?raw";
import businessContent from "@/content/services/kredyt-firmowy-sanok.md?raw";
import refinancingContent from "@/content/services/refinansowanie-kredytu-sanok.md?raw";

type RelatedLink = { href: string; label: string };

const services: Record<string, { content: string; related: RelatedLink[] }> = {
  "kredyt-hipoteczny-sanok": {
    content: mortgageContent,
    related: [
      { href: "/narzedzia/zdolnosc-kredytowa", label: "Sprawdź kalkulator zdolności kredytowej" },
      { href: "/refinansowanie-kredytu-sanok", label: "Zobacz, kiedy opłaca się refinansowanie" },
      { href: "/narzedzia/nadplata-kredytu", label: "Policz efekt nadpłaty kredytu" },
    ],
  },
  "kredyt-gotowkowy-sanok": {
    content: cashContent,
    related: [
      { href: "/narzedzia/konsolidacja", label: "Policz wariant konsolidacji zobowiązań" },
      { href: "/narzedzia/zdolnosc-kredytowa", label: "Sprawdź orientacyjną zdolność kredytową" },
      { href: "/kontakt", label: "Umów bezpłatną analizę ofert" },
    ],
  },
  "kredyt-firmowy-sanok": {
    content: businessContent,
    related: [
      { href: "/narzedzia/leasing-vs-kredyt", label: "Porównaj leasing z kredytem" },
      { href: "/narzedzia/kredyt-firmowy", label: "Oblicz orientacyjną ratę dla firmy" },
      { href: "/dla-firm", label: "Poznaj pełną ofertę finansowania firm" },
    ],
  },
  "refinansowanie-kredytu-sanok": {
    content: refinancingContent,
    related: [
      { href: "/narzedzia/refinansowanie", label: "Skorzystaj z kalkulatora refinansowania" },
      { href: "/narzedzia/nadplata-kredytu", label: "Porównaj refinansowanie z nadpłatą" },
      { href: "/kredyt-hipoteczny-sanok", label: "Zobacz wsparcie przy kredycie hipotecznym" },
    ],
  },
};

function renderInline(text: string): ReactNode[] {
  const tokens = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return tokens.map((token, index) => {
    const bold = token.match(/^\*\*(.+)\*\*$/);
    if (bold) return <strong key={index} className="font-bold text-foreground-900">{bold[1]}</strong>;
    const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return <a key={index} href={link[2]} className="font-semibold text-primary-600 underline underline-offset-4 hover:text-primary-700">{link[1]}</a>;
    return <Fragment key={index}>{token}</Fragment>;
  });
}

function extractFaqs(content: string) {
  const faqSection = content.split("## Najczęściej zadawane pytania")[1]?.split(/\n## /)[0] ?? "";
  return [...faqSection.matchAll(/\*\*(.+?)\*\*\n([\s\S]*?)(?=\n\n\*\*|$)/g)].map((match) => ({
    question: match[1].trim(),
    answer: match[2].trim(),
  }));
}

function MarkdownContent({ content }: { content: string }) {
  const blocks = content.trim().split(/\n\s*\n/);
  let inFaq = false;
  let paragraphIndex = 0;

  return blocks.map((block, index) => {
    if (block.startsWith("# ")) {
      return <h1 key={index} className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight text-foreground-950">{block.slice(2)}</h1>;
    }
    if (block.startsWith("## ")) {
      const heading = block.slice(3);
      inFaq = heading === "Najczęściej zadawane pytania";
      return <h2 key={index} className="mt-12 mb-5 text-2xl md:text-3xl font-bold text-foreground-950">{heading}</h2>;
    }
    if (inFaq) {
      const faq = block.match(/^\*\*(.+?)\*\*\n([\s\S]+)$/);
      if (faq) return <section key={index} className="mb-4 rounded-xl border border-background-200 bg-background-50 p-5"><h3 className="text-lg font-bold text-foreground-900">{faq[1]}</h3><p className="mt-3 leading-7 text-foreground-600">{renderInline(faq[2])}</p></section>;
    }
    paragraphIndex += 1;
    return <p key={index} className={paragraphIndex === 1 ? "mt-5 text-lg md:text-xl leading-8 text-foreground-600" : "mb-5 text-base leading-8 text-foreground-700"}>{renderInline(block.replace(/\n/g, " "))}</p>;
  });
}

export default function ServiceLandingPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const service = services[slug];
  if (!service) return <Navigate to="/404" replace />;

  const title = service.content.match(/^# (.+)$/m)?.[1] ?? slug;
  const lead = service.content.split(/\n\s*\n/)[1]?.replace(/\n/g, " ") ?? "";
  const faqs = extractFaqs(service.content);
  const url = `https://kredytpodkarpacie.pl/${slug}`;
  const schema = { "@context": "https://schema.org", "@graph": [
    { "@type": "Service", name: title, description: lead, url, provider: { "@type": "FinancialService", name: "EXPERT KREDYTOWY", address: { "@type": "PostalAddress", streetAddress: "ul. Dworcowa 11A", postalCode: "38-500", addressLocality: "Sanok", addressCountry: "PL" } }, areaServed: ["Sanok", "Zagórz", "Brzozów", "Lesko", "Ustrzyki Dolne", "Dynów", "Bieszczady", "Podkarpacie"] },
    { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } })) },
  ] };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <JsonLD data={schema} />
      <Navbar />
      <main className="flex-1 pt-28 md:pt-32 px-4 md:px-8 pb-16">
        <article className="max-w-5xl mx-auto bg-white border border-background-200 rounded-2xl shadow-sm p-6 md:p-10 lg:p-14">
          <Breadcrumb />
          <p className="text-center text-xs font-bold tracking-[0.2em] uppercase text-primary-500">Ekspert Kredytowy · Sanok i Podkarpacie</p>
          <div className="max-w-3xl mx-auto"><MarkdownContent content={service.content} /></div>

          <aside className="mt-12 rounded-2xl border border-primary-100 bg-primary-50 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-foreground-950">Powiązane narzędzia i informacje</h2>
            <ul className="mt-5 grid gap-3 md:grid-cols-3">
              {service.related.map((link) => <li key={link.href}><a href={link.href} className="flex h-full items-center rounded-xl bg-white border border-background-200 px-4 py-4 font-semibold text-primary-600 hover:border-primary-300 hover:text-primary-700">{link.label}</a></li>)}
            </ul>
          </aside>
        </article>
      </main>
      <Footer />
    </div>
  );
}
